import React from "react";
import { Box, Typography } from "@mui/material";
import { QuestionCard } from "../shared/QuestionCard";
import { QuizChoiceCard, ChoiceOption } from "../form/QuizChoiceCard";
import { FeedingGoal, FEEDING_GOAL_LABEL } from "@/helpers/enums";
import { dogPossessive } from "@/helpers/dogInclusion";
import { quizAsset } from "../shared/quizAssets";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  dogName: string;
  /** Sex drives his/her in the weight-management reassurance. */
  sex?: unknown;
  value?: string | null;
  onChange: (value: FeedingGoal) => void;
};

const OPTIONS: ChoiceOption[] = [
  { value: FeedingGoal.SKIN_COAT, label: FEEDING_GOAL_LABEL[FeedingGoal.SKIN_COAT], icon: quizAsset("icon-goal-coat") },
  { value: FeedingGoal.DIGESTION, label: FEEDING_GOAL_LABEL[FeedingGoal.DIGESTION], icon: quizAsset("icon-goal-digestion") },
  { value: FeedingGoal.WEIGHT, label: FEEDING_GOAL_LABEL[FeedingGoal.WEIGHT], icon: quizAsset("icon-goal-weight") },
  { value: FeedingGoal.ENERGY, label: FEEDING_GOAL_LABEL[FeedingGoal.ENERGY], icon: quizAsset("icon-goal-energy") },
  { value: FeedingGoal.FUSSY_MEALTIMES, label: FEEDING_GOAL_LABEL[FeedingGoal.FUSSY_MEALTIMES], icon: quizAsset("icon-goal-mealtimes") },
  { value: FeedingGoal.LONGEVITY, label: FEEDING_GOAL_LABEL[FeedingGoal.LONGEVITY], icon: quizAsset("icon-goal-longevity") },
  { value: FeedingGoal.OVERALL, label: FEEDING_GOAL_LABEL[FeedingGoal.OVERALL], icon: quizAsset("icon-goal-overall") },
];

/** Issue #52 A4 - exactly one matched reassurance renders, inline directly
 *  below the selected answer so it can't be missed below the fold. */
function reassurance(
  value: string | null | undefined,
  dogName: string,
  sex: unknown
): { headline: string; body: string } | null {
  const dog = dogName || "your dog";
  const poss = dogPossessive(sex); // his / her / their
  switch (value) {
    case FeedingGoal.SKIN_COAT:
      return {
        headline: "Less itching and dryness, more shine.",
        body: `Every single meal contains Salmon oil in our superfood supplement - exactly what ${dog}'s skin and coat need.`,
      };
    case FeedingGoal.DIGESTION:
      return {
        headline: "Firmer poos, fast.",
        body: "Gently cooked whole food is highly digestible. Better food in, better poos and less tummy troubles out.",
      };
    case FeedingGoal.WEIGHT:
      return {
        headline: `Portioned for ${poss} perfect weight.`,
        body: `Every meal measured to ${dog}'s exact calories. No guesswork.`,
      };
    case FeedingGoal.ENERGY:
      return {
        headline: "Puppy energy, at any age.",
        body: "Real meat and veg, no fillers. Owners tell us the difference shows within weeks.",
      };
    case FeedingGoal.FUSSY_MEALTIMES:
      return {
        headline: "Expect the bowl licked clean.",
        body: "Fresh food smells and tastes like real food, so even committed fusspots come running.",
      };
    case FeedingGoal.LONGEVITY:
      return {
        headline: "Healthier food, more time together.",
        body: `What's in ${dog}'s bowl every day is one of the few parts of ageing you control.`,
      };
    case FeedingGoal.OVERALL:
      return {
        headline: "One change, better across the board.",
        body: "Better skin, firmer poos, steadier weight and more energy, all from the same bowl.",
      };
    default:
      return null;
  }
}

export function GoalsQuestion({ dogName, sex, value, onChange }: Props) {
  const line = reassurance(value, dogName, sex);

  return (
    <QuestionCard
      title={`What are you most hoping Paws fresh food will do for ${dogName || "your dog"}?`}
    >
      <QuizChoiceCard
        options={OPTIONS}
        selected={value ?? ""}
        onSelect={(v) => onChange(v as FeedingGoal)}
        size="sm"
        afterSelected={
          line ? (
            <Box
              sx={{
                my: 1,
                mx: "auto",
                maxWidth: 460,
                bgcolor: QUIZ.color.cream2,
                borderRadius: "12px",
                px: "16px",
                py: "12px",
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 700,
                  fontSize: { xs: 14.5, md: 15.5 },
                  color: QUIZ.color.ink,
                  lineHeight: 1.35,
                  mb: "4px",
                }}
              >
                {line.headline}
              </Typography>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontSize: { xs: 14, md: 15 },
                  color: QUIZ.color.ink,
                  opacity: 0.85,
                  lineHeight: 1.45,
                }}
              >
                {line.body}
              </Typography>
            </Box>
          ) : null
        }
      />
    </QuestionCard>
  );
}
